import { useEffect, useMemo, useState } from "react";

import BedtimeIcon from "@mui/icons-material/Bedtime";
import NightsStayIcon from "@mui/icons-material/NightsStay";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import RestoreIcon from "@mui/icons-material/Restore";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  type PreferenceRow,
  getNotificationPreferences,
  getNotificationSnooze,
  getQuietHours,
  setNotificationSnooze,
  setQuietHours,
  updateNotificationPreferences,
} from "@/api/notificationPrefs";

type DirtyMap = Record<string, PreferenceRow>;

export function NotificationPreferencesPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const prefs = useQuery({
    queryKey: ["notification-preferences"],
    queryFn: getNotificationPreferences,
  });

  const [dirty, setDirty] = useState<DirtyMap>({});
  const [saved, setSaved] = useState(false);

  // Reset local edits when server state arrives
  useEffect(() => {
    if (prefs.data) {
      setDirty({});
    }
  }, [prefs.data]);

  const rows = useMemo<PreferenceRow[]>(
    () =>
      (prefs.data?.items ?? []).map((server) =>
        dirty[server.kind]
          ? { ...server, ...dirty[server.kind] }
          : server,
      ),
    [prefs.data, dirty],
  );

  const hasChanges = Object.keys(dirty).length > 0;

  const setRow = (row: PreferenceRow, patch: Partial<PreferenceRow>) => {
    setDirty((d) => ({
      ...d,
      [row.kind]: { ...row, ...patch, is_default: false },
    }));
  };

  const save = useMutation({
    mutationFn: () =>
      updateNotificationPreferences(
        rows.map((r) => ({
          kind: r.kind,
          inapp_enabled: r.inapp_enabled,
          email_enabled: r.email_enabled,
        })),
      ),
    onSuccess: () => {
      setSaved(true);
      setDirty({});
      qc.invalidateQueries({ queryKey: ["notification-preferences"] });
      setTimeout(() => setSaved(false), 3000);
    },
  });

  const resetAll = () => setDirty({});

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              <NotificationsActiveIcon
                fontSize="small"
                sx={{ verticalAlign: "middle", mr: 1 }}
              />
              {t("notifPrefs.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("notifPrefs.subtitle")}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<RestoreIcon />}
              onClick={resetAll}
              disabled={!hasChanges}
            >
              {t("notifPrefs.reset")}
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={() => save.mutate()}
              disabled={!hasChanges || save.isPending}
            >
              {t("notifPrefs.save")}
            </Button>
          </Stack>
        </Stack>

        {saved && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {t("notifPrefs.saved")}
          </Alert>
        )}

        <NotificationSnoozeRow />
        <Divider sx={{ my: 2 }} />
        <QuietHoursRow />
        <Divider sx={{ my: 2 }} />

        {prefs.isLoading ? (
          <CircularProgress size={20} />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("notifPrefs.col.kind")}</TableCell>
                <TableCell align="center">{t("notifPrefs.col.inapp")}</TableCell>
                <TableCell align="center">{t("notifPrefs.col.email")}</TableCell>
                <TableCell align="center">{t("notifPrefs.col.status")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.kind}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {t(`notifPrefs.kind.${r.kind}`, { defaultValue: r.kind })}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={r.inapp_enabled}
                      onChange={(e) =>
                        setRow(r, { inapp_enabled: e.target.checked })
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={r.email_enabled}
                      onChange={(e) =>
                        setRow(r, { email_enabled: e.target.checked })
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    {dirty[r.kind] ? (
                      <Chip size="small" color="warning" label={t("notifPrefs.modified")} />
                    ) : r.is_default ? (
                      <Tooltip title={t("notifPrefs.defaultTooltip")}>
                        <Chip size="small" variant="outlined" label={t("notifPrefs.default")} />
                      </Tooltip>
                    ) : (
                      <Chip size="small" color="info" label={t("notifPrefs.custom")} />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}


function NotificationSnoozeRow() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const snooze = useQuery({
    queryKey: ["notification-snooze"],
    queryFn: getNotificationSnooze,
  });

  const [duration, setDuration] = useState<string>("8h");

  const mut = useMutation({
    mutationFn: (until: string | null) => setNotificationSnooze(until),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notification-snooze"] }),
  });

  const active = snooze.data?.active ?? false;
  const until = snooze.data?.until ?? null;

  const applySnooze = () => {
    const now = new Date();
    let ms = 0;
    if (duration === "1h") ms = 60 * 60_000;
    else if (duration === "8h") ms = 8 * 60 * 60_000;
    else if (duration === "1d") ms = 24 * 60 * 60_000;
    else if (duration === "7d") ms = 7 * 24 * 60 * 60_000;
    const target = new Date(now.getTime() + ms);
    mut.mutate(target.toISOString());
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "flex-start", sm: "center" }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            <BedtimeIcon
              fontSize="small"
              sx={{ verticalAlign: "middle", mr: 1 }}
            />
            {t("notifPrefs.snoozeTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {active && until
              ? t("notifPrefs.snoozeUntil", {
                  date: new Date(until).toLocaleString(),
                })
              : t("notifPrefs.snoozeHint")}
          </Typography>
        </Box>
        {!active && (
          <>
            <TextField
              select
              size="small"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="1h">{t("notifPrefs.dur.1h")}</MenuItem>
              <MenuItem value="8h">{t("notifPrefs.dur.8h")}</MenuItem>
              <MenuItem value="1d">{t("notifPrefs.dur.1d")}</MenuItem>
              <MenuItem value="7d">{t("notifPrefs.dur.7d")}</MenuItem>
            </TextField>
            <Button
              variant="outlined"
              startIcon={<BedtimeIcon />}
              onClick={applySnooze}
              disabled={mut.isPending}
            >
              {t("notifPrefs.snoozeApply")}
            </Button>
          </>
        )}
        {active && (
          <Button
            variant="outlined"
            color="warning"
            onClick={() => mut.mutate(null)}
            disabled={mut.isPending}
          >
            {t("notifPrefs.snoozeCancel")}
          </Button>
        )}
      </Stack>
    </Box>
  );
}


function _short(time?: string | null): string {
  if (!time) return "";
  return time.slice(0, 5);
}

function QuietHoursRow() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const qh = useQuery({
    queryKey: ["notification-quiet-hours"],
    queryFn: getQuietHours,
  });

  const [start, setStart] = useState("22:00");
  const [end, setEnd] = useState("06:00");

  useEffect(() => {
    if (qh.data?.start) setStart(_short(qh.data.start));
    if (qh.data?.end) setEnd(_short(qh.data.end));
  }, [qh.data]);

  const mut = useMutation({
    mutationFn: (vals: { start: string | null; end: string | null }) =>
      setQuietHours(vals.start, vals.end),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notification-quiet-hours"] }),
  });

  const enabled = qh.data?.enabled ?? false;

  return (
    <Box sx={{ mb: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "flex-start", sm: "center" }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            <NightsStayIcon
              fontSize="small"
              sx={{ verticalAlign: "middle", mr: 1 }}
            />
            {t("notifPrefs.quietTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {enabled
              ? t("notifPrefs.quietActive", {
                  start: _short(qh.data?.start),
                  end: _short(qh.data?.end),
                })
              : t("notifPrefs.quietHint")}
          </Typography>
        </Box>
        <TextField
          size="small"
          type="time"
          label={t("notifPrefs.quietStart")}
          value={start}
          onChange={(e) => setStart(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 130 }}
        />
        <TextField
          size="small"
          type="time"
          label={t("notifPrefs.quietEnd")}
          value={end}
          onChange={(e) => setEnd(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 130 }}
        />
        <Button
          variant={enabled ? "contained" : "outlined"}
          startIcon={<NightsStayIcon />}
          onClick={() =>
            mut.mutate({ start: `${start}:00`, end: `${end}:00` })
          }
          disabled={mut.isPending || start === end}
        >
          {enabled ? t("notifPrefs.quietUpdate") : t("notifPrefs.quietApply")}
        </Button>
        {enabled && (
          <Button
            variant="outlined"
            color="warning"
            onClick={() => mut.mutate({ start: null, end: null })}
            disabled={mut.isPending}
          >
            {t("notifPrefs.quietDisable")}
          </Button>
        )}
      </Stack>
    </Box>
  );
}
